import { useMemo, useState, memo, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  X, Phone, MessageCircle, Check, Gauge, User, MapPin, Mail, Users, Briefcase,
  Car, Building2, ShieldAlert, KeyRound, GraduationCap, HeartPulse, Banknote,
  CreditCard, Landmark, Cake, Plane, Camera, Hash, Fingerprint, BadgeCheck,
  FileText, Globe, Syringe, Scale, Award, Flag, Wallet, Receipt, Smartphone,
  ShoppingCart, Home, Search, Copy, ChevronDown, Sparkles, Skull, IdCard,
  Tv, Wifi, AlertTriangle, Vote, Plug, ListTree, ExternalLink
} from "lucide-react";

function proxiar(api, url) {
  return api ? `${api}/img?u=${encodeURIComponent(url)}` : url;
}

const CHAVES_IMAGEM = ["foto","fotos","fotowhatsapp","imagem","image","avatar","photo","picture","urlfoto","mapa","map","thumb","screenshot","print","capa"];
const ehChaveImagem = (k) => CHAVES_IMAGEM.some((c) => String(k).toLowerCase().includes(c));
const ehUrl = (v) => typeof v === "string" && /^https?:\/\//.test(v);
const ehUrlImagem = (v) =>
  typeof v === "string" &&
  /^https?:\/\//.test(v) &&
  /(reidasfotosbr\.com|googleusercontent\.com|streetviewpixels|ggpht\.com|staticmap|maps\.googleapis|gstatic|\.(jpe?g|png|webp|gif))/i.test(v);

const TITULOS = {
  cadastral: "Dados cadastrais", credenciaisVazadas: "Credenciais vazadas",
  escolaridade: "Escolaridade", genitores: "Genitores", certidoes: "Certidões",
  pecasBnmp: "Mandados (BNMP)", assinaturas: "Assinaturas", fotos: "Fotos",
  inscricoesOab: "Inscrições OAB", extraFotos: "Outras fotos",
  ppe: "Pessoa Politicamente Exposta", outrosNomes: "Outros nomes",
  chavesPix: "Chaves PIX", enderecos: "Endereços", telefones: "Telefones",
  emails: "E-mails", movimentacoesOnline: "Movimentações online",
  contasBancos: "Contas bancárias", dividaAtiva: "Dívida ativa",
  planos: "Planos", planosMoveis: "Planos móveis", profissoes: "Profissões",
  empregos: "Empregos", relacionadosPorEndereco: "Relacionados por endereço",
  parentes: "Parentes", placas: "Veículos / Placas",
  universitarios: "Universitários", emprestimos: "Empréstimos",
  beneficios: "Benefícios", sociedades: "Sociedades",
  contatosComerciais: "Contatos comerciais", historicoEscolar: "Histórico escolar",
  politica: "Política", dadosConselho: "Conselhos de classe",
  imoveisSp: "Imóveis (SP)", comprasOnline: "Compras online",
  propensoes: "Propensões", beneficiosAuxilios: "Benefícios e auxílios",
  pagamentosAuxilioEmergencial: "Auxílio emergencial", vacinas: "Vacinas",
  certidaoObito: "Certidão de óbito", irpf: "IRPF", estrangeiro: "Estrangeiro",
  sisu: "SISU", prouni: "ProUni", cnh: "CNH", rgs: "RGs",
  inssSiape: "INSS / SIAPE", linkedin: "LinkedIn", drones: "Drones",
  ccf: "Cheques sem fundo (CCF)", rais: "RAIS", viagens: "Viagens",
  meiDetalhado: "MEI", cin: "CIN", vazamentos: "Vazamentos",
  planosSaude: "Planos de saúde", energias: "Contas de energia",
  empresasRelacionadas: "Empresas relacionadas", aeronaves: "Aeronaves",
  processos: "Processos", vinculosPorProcessos: "Vínculos por processos",
};

const ICONES = {
  cadastral: User, fotos: Camera, extraFotos: Camera, enderecos: MapPin,
  telefones: Phone, emails: Mail, parentes: Users, genitores: Users,
  profissoes: Briefcase, empregos: Briefcase, sociedades: Building2,
  empresasRelacionadas: Building2, placas: Car, aeronaves: Plane,
  drones: Plane, viagens: Plane, credenciaisVazadas: ShieldAlert,
  vazamentos: ShieldAlert, chavesPix: KeyRound, escolaridade: GraduationCap,
  universitarios: GraduationCap, historicoEscolar: GraduationCap,
  sisu: GraduationCap, prouni: GraduationCap, planosSaude: HeartPulse,
  vacinas: Syringe, beneficios: Wallet, beneficiosAuxilios: Wallet,
  pagamentosAuxilioEmergencial: Wallet, emprestimos: Banknote,
  contasBancos: Landmark, irpf: Receipt, dividaAtiva: AlertTriangle,
  ccf: AlertTriangle, planos: Tv, planosMoveis: Smartphone,
  comprasOnline: ShoppingCart, certidoes: FileText, certidaoObito: Skull,
  pecasBnmp: Scale, processos: Scale, vinculosPorProcessos: Scale,
  assinaturas: FileText, inscricoesOab: Award, dadosConselho: Award,
  cnh: IdCard, rgs: IdCard, cin: IdCard, inssSiape: BadgeCheck,
  linkedin: Globe, contatosComerciais: Phone, relacionadosPorEndereco: Home,
  imoveisSp: Home, energias: Plug, movimentacoesOnline: Globe,
  ppe: Flag, politica: Vote, estrangeiro: Globe, propensoes: Sparkles,
  outrosNomes: User, meiDetalhado: Briefcase, rais: FileText,
};

const LABELS = {
  cpf: "CPF", cpfMask: "CPF", cnpj: "CNPJ", nome: "Nome",
  nomeSocial: "Nome social", mae: "Mãe", pai: "Pai", sexo: "Sexo",
  dataNasc: "Nascimento", idade: "Idade", renda: "Renda",
  escolaridade: "Escolaridade", classeSocial: "Classe social",
  naturalidade: "Naturalidade", endereco: "Logradouro", numero: "Número",
  complemento: "Complemento", bairro: "Bairro", cidade: "Cidade",
  uf: "UF", cep: "CEP", telefone: "Telefone", email: "E-mail",
  password: "Senha", login: "Login", url: "URL", data: "Data",
  fonte: "Fonte", classificacao: "Classificação", signo: "Signo",
  signoChines: "Signo chinês", rg: "RG", tituloEleitor: "Título de eleitor",
  ddd: "DDD", tel: "Número", telfull: "Completo", chave: "Chave",
  banco: "Banco", agencia: "Agência", conta: "Conta", placa: "Placa",
  marca: "Marca", modelo: "Modelo", ano: "Ano", cor: "Cor",
  combustivel: "Combustível", chassi: "Chassi", renavam: "Renavam",
  municipio: "Município", razaoSocial: "Razão social",
  nomeFantasia: "Nome fantasia", situacao: "Situação", capital: "Capital",
  participacao: "Participação", cargo: "Cargo", empresa: "Empresa",
  parentesco: "Parentesco", grau: "Grau", profissao: "Profissão",
  salario: "Salário", admissao: "Admissão", demissao: "Demissão",
};

const prettify = (chave) => String(chave).replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
const rotulo = (chave) => LABELS[chave] || prettify(chave);

function vazio(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0 || v.every(vazio);
  if (typeof v === "object") {
    const vals = Object.values(v);
    return vals.length === 0 || vals.every(vazio);
  }
  return false;
}

function textoProfundo(v) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  if (ehUrlImagem(v)) return "";
  if (typeof v !== "object") return String(v);
  if (Array.isArray(v)) {
    return v.map((x) => (x && typeof x === "object" ? textoProfundo(x) : ehUrlImagem(x) ? "" : String(x)))
      .filter((s) => s && s !== "—").join(" · ");
  }
  return Object.entries(v)
    .filter(([, val]) => !vazio(val) && !ehUrlImagem(val))
    .map(([k, val]) => {
      const t = typeof val === "object" ? textoProfundo(val) : val;
      return t ? `${rotulo(k)}: ${t}` : "";
    }).filter(Boolean).join(" · ");
}

function coletarImagens(v, acc) {
  acc = acc || [];
  if (ehUrlImagem(v)) acc.push(v);
  else if (Array.isArray(v)) v.forEach((x) => coletarImagens(x, acc));
  else if (v && typeof v === "object") Object.values(v).forEach((x) => coletarImagens(x, acc));
  return acc;
}

function copiar(texto) {
  if (!texto) return;
  try { navigator.clipboard.writeText(String(texto)); } catch (_) {}
}

const Pill = ({ children, tone = "slate", icon: Icon }) => {
  const tones = {
    slate: "bg-white/10 text-slate-200 ring-white/10",
    teal: "bg-teal-400/15 text-teal-200 ring-teal-400/30",
    rose: "bg-rose-500/15 text-rose-200 ring-rose-500/30",
    amber: "bg-amber-400/15 text-amber-200 ring-amber-400/30",
    emerald: "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30",
    violet: "bg-violet-500/15 text-violet-200 ring-violet-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ${tones[tone]}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </span>
  );
};

function Campo({ k, val, api }) {
  const Icon = null;
  if (ehChaveImagem(k) && ehUrl(val)) {
    return (
      <div className="col-span-2">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{rotulo(k)}</p>
        <img src={proxiar(api, val)} alt={rotulo(k)} loading="lazy"
          className="w-full rounded-xl bg-black/40 object-cover ring-1 ring-white/10" />
      </div>
    );
  }
  if (ehChaveImagem(k) && Array.isArray(val) && val.some(ehUrl)) {
    return (
      <div className="col-span-2">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{rotulo(k)}</p>
        <div className="flex flex-col gap-2">
          {val.filter(ehUrl).map((u, ix) => (
            <img key={ix} src={proxiar(api, u)} alt="" loading="lazy"
              className="w-full rounded-xl bg-black/40 object-cover ring-1 ring-white/10" />
          ))}
        </div>
      </div>
    );
  }

  let display;
  if (val === null || val === undefined || val === "") display = <span className="text-slate-600">—</span>;
  else if (typeof val === "boolean") display = <span className="text-slate-100">{val ? "Sim" : "Não"}</span>;
  else if (typeof val !== "object") display = <span className="break-words text-slate-100">{String(val)}</span>;
  else {
    const imgs = coletarImagens(val);
    const txt = textoProfundo(val);
    display = (
      <div className="flex flex-col gap-2">
        {imgs.length > 0 && (
          <div className="flex flex-col gap-2">
            {imgs.map((u, ix) => (
              <img key={ix} src={proxiar(api, u)} alt="" loading="lazy"
                className="w-full rounded-xl bg-black/30 object-cover ring-1 ring-white/10" />
            ))}
          </div>
        )}
        {txt ? <span className="break-words leading-relaxed text-slate-200">{txt}</span> :
          imgs.length === 0 && <span className="text-slate-600">—</span>}
      </div>
    );
  }

  const isCopyable = typeof val === "string" && /^[\w@.\-+()/ ]+$/.test(val) && val.length < 80;
  return (
    <div className="group min-w-0">
      <p className="mb-0.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {rotulo(k)}
      </p>
      <div className="flex items-start gap-2 text-sm">
        <div className="min-w-0 flex-1">{display}</div>
        {isCopyable && (
          <button onClick={() => copiar(val)}
            className="shrink-0 rounded-md p-1 text-slate-500 opacity-0 transition-opacity hover:bg-white/10 hover:text-teal-300 group-hover:opacity-100"
            title="Copiar">
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function ItemObjeto({ obj, api }) {
  const itens = Object.entries(obj).filter(([, val]) => !vazio(val));
  if (itens.length === 0) return <span className="text-slate-600">—</span>;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
      {itens.map(([k, val]) => <Campo key={k} k={k} val={val} api={api} />)}
    </div>
  );
}

function ConteudoSecao({ valor, api }) {
  if (Array.isArray(valor)) {
    return (
      <div className="flex flex-col gap-2">
        {valor.map((item, i) => (
          <div key={i} className="rounded-xl border border-white/5 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-3">
            {item && typeof item === "object" ? <ItemObjeto obj={item} api={api} /> :
              <span className="text-slate-100">{String(item)}</span>}
          </div>
        ))}
      </div>
    );
  }
  if (valor && typeof valor === "object") return <ItemObjeto obj={valor} api={api} />;
  if (valor === null || valor === undefined || valor === "") return <span className="text-slate-600">—</span>;
  return <span className="text-slate-100">{String(valor)}</span>;
}

function GradeFotos({ lista, api }) {
  const fotos = (lista || [])
    .map((f) => (typeof f === "string" ? f : (f && (f.foto || f.url || f.imagem))))
    .filter(Boolean);
  if (fotos.length === 0) return <span className="text-slate-600">—</span>;
  return (
    <div className="flex flex-col gap-3">
      {fotos.map((url, i) => (
        <a key={i} href={proxiar(api, url)} target="_blank" rel="noreferrer"
          className="group relative overflow-hidden rounded-xl ring-1 ring-white/10 transition-all hover:ring-teal-400/60">
          <img src={proxiar(api, url)} alt={`Foto ${i + 1}`} loading="lazy"
            className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
          <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-0.5 text-[11px] font-bold text-white">
            #{i + 1}
          </span>
        </a>
      ))}
    </div>
  );
}

function CardsTelefones({ lista }) {
  return (
    <div className="grid grid-cols-1 gap-2">
      {(lista || []).map((t, i) => {
        const digitos = String(t.telfull || `${t.ddd || ""}${t.tel || ""}`).replace(/\D/g, "");
        const wa = digitos ? `https://wa.me/55${digitos}` : null;
        const tel = digitos ? `tel:+55${digitos}` : null;
        return (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.04] p-3 transition-colors hover:bg-white/[0.07]">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-emerald-400/30 to-emerald-600/10 ring-1 ring-emerald-400/30">
              <Phone className="h-5 w-5 text-emerald-300" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-sm font-semibold text-white">{t.telefone || digitos}</p>
              <div className="mt-0.5 flex flex-wrap gap-1.5 text-[10px] text-slate-400">
                {t.operadora && <Pill tone="slate">{t.operadora}</Pill>}
                {t.tipo && <Pill tone="slate">{t.tipo}</Pill>}
                {t.classificacao && <Pill tone="teal">Classe {t.classificacao}</Pill>}
                {t.data && <span>· {t.data}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {tel && <a href={tel} className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-slate-200 transition-colors hover:bg-white/20" title="Ligar"><Phone className="h-4 w-4" /></a>}
              {wa && <a href={wa} target="_blank" rel="noreferrer" className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-white transition-colors hover:bg-emerald-400" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>}
              <button onClick={() => copiar(digitos)} className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-slate-200 hover:bg-white/20" title="Copiar"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CardsEmails({ lista }) {
  return (
    <div className="flex flex-col gap-2">
      {(lista || []).map((e, i) => {
        const email = typeof e === "string" ? e : (e.email || e.valor || "");
        return (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.04] p-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-sky-400/30 to-sky-600/10 ring-1 ring-sky-400/30">
              <Mail className="h-4 w-4 text-sky-300" />
            </div>
            <p className="min-w-0 flex-1 truncate text-sm text-white">{email}</p>
            <button onClick={() => copiar(email)} className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-slate-200 hover:bg-white/20"><Copy className="h-3.5 w-3.5" /></button>
          </div>
        );
      })}
    </div>
  );
}

function CardsEnderecos({ lista, api }) {
  return (
    <div className="flex flex-col gap-2">
      {(lista || []).map((e, i) => (
        <div key={i} className="rounded-xl border border-white/5 bg-gradient-to-br from-white/[0.05] to-white/[0.01] p-3">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-teal-400/20 ring-1 ring-teal-400/40">
              <span className="font-display text-xs font-bold text-teal-200">{i + 1}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-snug text-white">
                {[e.endereco, e.numero && `nº ${e.numero}`, e.complemento].filter(Boolean).join(", ")}
              </p>
              <p className="mt-0.5 text-xs text-slate-400">
                {[e.bairro, e.cidade && `${e.cidade}${e.uf ? `/${e.uf}` : ""}`].filter(Boolean).join(" · ")}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {e.cep && <Pill tone="slate">CEP {e.cep}</Pill>}
                {e.classificacao && <Pill tone="teal">Classe {e.classificacao}</Pill>}
                {e.tipo && <Pill tone="slate">{e.tipo}</Pill>}
                {e.data && <Pill tone="slate">{e.data}</Pill>}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CardsParentes({ lista, api }) {
  return (
    <div className="grid grid-cols-1 gap-2">
      {(lista || []).map((p, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.04] p-3">
          {p.foto || p.fotoWhatsapp || p.imagem ? (
            <img src={proxiar(api, p.foto || p.fotoWhatsapp || p.imagem)} alt=""
              loading="lazy"
              className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-violet-400/40" />
          ) : (
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-gradient-to-br from-violet-400/30 to-violet-600/10 ring-1 ring-violet-400/30">
              <Users className="h-5 w-5 text-violet-200" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{p.nome || p.cpf || "—"}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
              {(p.parentesco || p.grau) && <Pill tone="violet">{p.parentesco || p.grau}</Pill>}
              {p.cpf && <span className="font-mono">{p.cpf}</span>}
              {p.idade && <span>· {p.idade} anos</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CardsVeiculos({ lista }) {
  return (
    <div className="grid grid-cols-1 gap-2">
      {(lista || []).map((v, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.04] p-3">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-amber-400/30 to-amber-600/10 ring-1 ring-amber-400/30">
            <Car className="h-5 w-5 text-amber-200" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="rounded-md bg-white px-2 py-0.5 font-mono text-xs font-bold tracking-widest text-slate-900">{v.placa || "—"}</span>
              {v.ano && <span className="text-xs text-slate-400">{v.ano}</span>}
            </div>
            <p className="mt-1 truncate text-sm text-white">
              {[v.marca, v.modelo].filter(Boolean).join(" ") || "—"}
            </p>
            <div className="mt-0.5 flex flex-wrap gap-1.5 text-[10px]">
              {v.cor && <Pill tone="slate">{v.cor}</Pill>}
              {v.combustivel && <Pill tone="slate">{v.combustivel}</Pill>}
              {v.municipio && <Pill tone="slate">{v.municipio}</Pill>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CardsSociedades({ lista, api }) {
  return (
    <div className="flex flex-col gap-2">
      {(lista || []).map((s, i) => (
        <div key={i} className="rounded-xl border border-white/5 bg-white/[0.04] p-3">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-cyan-400/30 to-cyan-600/10 ring-1 ring-cyan-400/30">
              <Building2 className="h-5 w-5 text-cyan-200" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">{s.razaoSocial || s.nomeFantasia || s.nome || "—"}</p>
              {s.cnpj && <p className="font-mono text-[11px] text-teal-300/80">{s.cnpj}</p>}
              <div className="mt-1.5">
                <ItemObjeto obj={Object.fromEntries(Object.entries(s).filter(([k]) => !["razaoSocial","nomeFantasia","nome","cnpj"].includes(k)))} api={api} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Propensoes({ obj }) {
  const flags = Object.entries(obj).filter(([k, v]) => (v === 0 || v === 1) && !["cpf", "csb8"].includes(k));
  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      {flags.map(([k, v]) => (
        <div key={k} className={`flex items-center gap-2 rounded-lg px-3 py-2 ring-1 ${v ? "bg-emerald-500/10 ring-emerald-500/30" : "bg-white/[0.03] ring-white/5"}`}>
          {v ? <Check className="h-4 w-4 shrink-0 text-emerald-400" /> : <X className="h-4 w-4 shrink-0 text-slate-600" />}
          <span className={`text-xs ${v ? "text-emerald-100" : "text-slate-500"}`}>{prettify(k)}</span>
        </div>
      ))}
    </div>
  );
}

function CardsCredenciais({ lista }) {
  return (
    <div className="flex flex-col gap-2">
      {(lista || []).map((c, i) => (
        <div key={i} className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-3">
          {(c.tipo || c.valor) && (
            <div className="mb-2 flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-rose-300" />
              <p className="text-sm font-semibold text-rose-100">
                {c.tipo ? `${c.tipo}: ` : ""}{c.valor || ""}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            {(c.resultados || []).map((r, j) => (
              <div key={j} className="rounded-lg bg-black/40 p-2.5 text-xs">
                {r.url && <p className="break-all"><span className="text-slate-500">URL: </span><span className="text-slate-200">{r.url}</span></p>}
                {r.login && <p className="break-all mt-0.5"><span className="text-slate-500">Login: </span><span className="text-slate-100">{r.login}</span></p>}
                {r.password && <p className="break-all mt-0.5"><span className="text-slate-500">Senha: </span><code className="rounded bg-rose-500/20 px-1.5 py-0.5 font-mono text-rose-200">{r.password}</code></p>}
                {r.file_date && <p className="mt-1 text-[10px] text-slate-500">{r.file_date}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function faixaScore(s) {
  if (s < 400) return { label: "Baixo", cor: "#ef4444", grad: "from-rose-500 to-rose-600" };
  if (s < 600) return { label: "Regular", cor: "#f59e0b", grad: "from-amber-400 to-orange-500" };
  if (s < 800) return { label: "Bom", cor: "#3b82f6", grad: "from-sky-400 to-blue-500" };
  return { label: "Excelente", cor: "#22c55e", grad: "from-emerald-400 to-emerald-500" };
}

function ScoreCard({ score }) {
  const f = faixaScore(score);
  const pct = Math.max(2, Math.min(100, (score / 1000) * 100));
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-4">
      <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-bold text-teal-200">
        <Gauge className="h-4 w-4" /> Score de Crédito
      </h3>
      <div className="flex items-end gap-3">
        <span className="font-display text-5xl font-extrabold leading-none" style={{ color: f.cor }}>{score}</span>
        <span className="mb-1 text-sm text-slate-400">/ 1000</span>
        <span className="mb-1 ml-auto rounded-full px-2.5 py-1 text-xs font-bold ring-1"
          style={{ color: f.cor, backgroundColor: f.cor + "22", borderColor: f.cor + "55" }}>{f.label}</span>
      </div>
      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
        <div className={`h-full rounded-full bg-gradient-to-r ${f.grad}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-slate-500">
        <span>0</span><span>400</span><span>600</span><span>800</span><span>1000</span>
      </div>
    </section>
  );
}

// Hero do cadastro (formato dedicado)
function HeroCadastral({ cad, foto, api, score }) {
  if (!cad) return null;
  const idade = cad.idade;
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-teal-500/10 via-white/[0.03] to-white/[0.01] p-4">
      <div className="flex items-start gap-4">
        {foto ? (
          <img src={proxiar(api, foto)} alt="" className="h-24 w-24 shrink-0 rounded-2xl object-cover ring-2 ring-teal-400/60 shadow-lg shadow-teal-500/20" />
        ) : (
          <div className="grid h-24 w-24 shrink-0 place-items-center rounded-2xl bg-white/10 ring-1 ring-white/10">
            <User className="h-10 w-10 text-slate-400" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-extrabold leading-tight text-white">{cad.nome || "—"}</p>
          {cad.nomeSocial && cad.nomeSocial !== cad.nome && (
            <p className="text-xs italic text-slate-400">{cad.nomeSocial}</p>
          )}
          {cad.cpfMask && (
            <button onClick={() => copiar(cad.cpfMask)} className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-teal-400/10 px-2 py-0.5 font-mono text-xs text-teal-200 ring-1 ring-teal-400/30 transition-colors hover:bg-teal-400/20">
              <Fingerprint className="h-3 w-3" /> {cad.cpfMask} <Copy className="h-3 w-3" />
            </button>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {cad.sexo && <Pill tone="slate" icon={cad.sexo === "F" ? User : User}>{cad.sexo === "M" ? "Masculino" : cad.sexo === "F" ? "Feminino" : cad.sexo}</Pill>}
            {idade && <Pill tone="teal">{idade} anos</Pill>}
            {cad.dataNasc && <Pill tone="slate" icon={Cake}>{cad.dataNasc}</Pill>}
            {cad.signo && <Pill tone="violet">{cad.signo}</Pill>}
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-3 text-xs">
        {cad.mae?.nome && <InfoLine icon={Users} label="Mãe" value={cad.mae.nome} />}
        {cad.pai?.nome && <InfoLine icon={Users} label="Pai" value={cad.pai.nome} />}
        {cad.naturalidade && <InfoLine icon={Flag} label="Natural de" value={cad.naturalidade} />}
        {cad.escolaridade && <InfoLine icon={GraduationCap} label="Escolaridade" value={cad.escolaridade} />}
        {cad.renda && <InfoLine icon={Banknote} label="Renda" value={cad.renda} />}
        {cad.classeSocial && <InfoLine icon={Award} label="Classe" value={cad.classeSocial} />}
      </div>
    </section>
  );
}

function InfoLine({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-300/70" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className="truncate font-medium text-slate-100">{value}</p>
      </div>
    </div>
  );
}

const ORDEM = [
  "cadastral","fotos","enderecos","telefones","emails","chavesPix",
  "parentes","genitores","outrosNomes",
  "profissoes","empregos","sociedades","empresasRelacionadas","meiDetalhado",
  "placas","aeronaves","drones","viagens",
  "renda","contasBancos","beneficios","beneficiosAuxilios","pagamentosAuxilioEmergencial","emprestimos","irpf",
  "escolaridade","universitarios","historicoEscolar","sisu","prouni",
  "planosSaude","vacinas","planos","planosMoveis","energias",
  "cnh","rgs","cin","tituloEleitor",
  "credenciaisVazadas","vazamentos","dividaAtiva","ccf",
  "processos","pecasBnmp","certidoes","certidaoObito","vinculosPorProcessos",
  "inscricoesOab","dadosConselho","politica","ppe",
  "comprasOnline","movimentacoesOnline","contatosComerciais",
  "imoveisSp","relacionadosPorEndereco","inssSiape","linkedin",
  "rais","estrangeiro","assinaturas","propensoes","extraFotos",
];

// Agrupamento em "abas" para navegação rápida
const GRUPOS = [
  { id: "resumo", label: "Resumo", icon: User, keys: ["cadastral","propensoes"] },
  { id: "contato", label: "Contato", icon: Phone, keys: ["telefones","emails","enderecos","chavesPix"] },
  { id: "pessoas", label: "Pessoas", icon: Users, keys: ["parentes","genitores","outrosNomes","relacionadosPorEndereco"] },
  { id: "trabalho", label: "Trabalho", icon: Briefcase, keys: ["profissoes","empregos","sociedades","empresasRelacionadas","meiDetalhado","rais","linkedin","contatosComerciais"] },
  { id: "financas", label: "Finanças", icon: Banknote, keys: ["renda","contasBancos","beneficios","beneficiosAuxilios","pagamentosAuxilioEmergencial","emprestimos","irpf","dividaAtiva","ccf"] },
  { id: "veiculos", label: "Veículos", icon: Car, keys: ["placas","aeronaves","drones","viagens"] },
  { id: "educacao", label: "Educação", icon: GraduationCap, keys: ["escolaridade","universitarios","historicoEscolar","sisu","prouni","inscricoesOab","dadosConselho"] },
  { id: "saude", label: "Saúde", icon: HeartPulse, keys: ["planosSaude","vacinas","certidaoObito"] },
  { id: "docs", label: "Documentos", icon: IdCard, keys: ["cnh","rgs","cin","tituloEleitor","certidoes","assinaturas","fotos","extraFotos"] },
  { id: "juridico", label: "Jurídico", icon: Scale, keys: ["processos","pecasBnmp","vinculosPorProcessos","politica","ppe","estrangeiro"] },
  { id: "servicos", label: "Serviços", icon: Tv, keys: ["planos","planosMoveis","energias","comprasOnline","movimentacoesOnline","imoveisSp","inssSiape"] },
  { id: "vazamentos", label: "Vazamentos", icon: ShieldAlert, keys: ["credenciaisVazadas","vazamentos"] },
];

const SecaoRender = memo(function SecaoRender({ k, valor, api }) {
  if (k === "fotos" || k === "extraFotos") return <GradeFotos lista={valor} api={api} />;
  if (k === "telefones") return <CardsTelefones lista={valor} />;
  if (k === "emails") return <CardsEmails lista={valor} />;
  if (k === "enderecos") return <CardsEnderecos lista={valor} api={api} />;
  if (k === "parentes" || k === "genitores") return <CardsParentes lista={Array.isArray(valor) ? valor : [valor]} api={api} />;
  if (k === "placas") return <CardsVeiculos lista={valor} />;
  if (k === "sociedades" || k === "empresasRelacionadas") return <CardsSociedades lista={valor} api={api} />;
  if (k === "propensoes") return <Propensoes obj={valor} />;
  if (k === "credenciaisVazadas") return <CardsCredenciais lista={valor} />;
  return <ConteudoSecao valor={valor} api={api} />;
});

function Secao({ id, k, valor, api, count, innerRef }) {
  const Icon = ICONES[k] || FileText;
  const titulo = TITULOS[k] || prettify(k);

  return (
    <section ref={innerRef} id={id} className="scroll-mt-20 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
      <div className="flex w-full items-center gap-3 border-b border-white/10 bg-gradient-to-r from-white/[0.06] to-transparent px-4 py-2.5">
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-teal-400/15 ring-1 ring-teal-400/30">
          <Icon className="h-4 w-4 text-teal-300" />
        </div>
        <h3 className="flex-1 font-display text-sm font-bold text-white">{titulo}</h3>
        {count !== null && count !== undefined && <Pill tone="teal">{count}</Pill>}
      </div>
      <div className="p-4"><SecaoRender k={k} valor={valor} api={api} /></div>
    </section>
  );
}

export default function DetalhesModal({ dados, api, foto, nome, onClose }) {
  const [tocOpen, setTocOpen] = useState(false);
  const scrollerRef = useRef(null);

  const chavesOrdenadas = useMemo(() => {
    if (!dados) return [];
    const presentes = Object.keys(dados).filter((k) => !vazio(dados[k]) && k !== "docsBase64");
    const setP = new Set(presentes);
    const ordenadas = ORDEM.filter((k) => setP.has(k));
    const extras = presentes.filter((k) => !ORDEM.includes(k));
    return [...ordenadas, ...extras];
  }, [dados]);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el && scrollerRef.current) {
      scrollerRef.current.scrollTo({ top: el.offsetTop - 12, behavior: "smooth" });
    }
    setTocOpen(false);
  }, []);

  if (!dados) return null;

  const cad = dados.cadastral || {};
  const scoreNum = dados.propensoes && dados.propensoes.csb8 ? Number(dados.propensoes.csb8) : null;

  const countOf = (k) => {
    const v = dados[k];
    if (Array.isArray(v)) return v.length;
    return null;
  };

  return (
    <motion.div
      initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
      transition={{ type: "tween", duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-y-0 right-0 z-[90] flex w-full flex-col bg-[#070c18] shadow-2xl shadow-black/60 sm:max-w-md"
      data-testid="detalhes-modal"
      onClick={(e) => e.stopPropagation()}
    >
      {/* HEADER */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-[#04070f] to-[#070c18] px-3 py-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-3">
          {foto ? (
            <img src={proxiar(api, foto)} alt="" className="h-11 w-11 rounded-full object-cover ring-2 ring-teal-400/60" />
          ) : (
            <div className="grid h-11 w-11 place-items-center rounded-full bg-white/10"><User className="h-5 w-5 text-slate-400" /></div>
          )}
          <div className="min-w-0">
            <p className="truncate font-display text-[15px] font-bold leading-tight text-white">{nome || "Detalhes"}</p>
            <p className="text-[11px] text-slate-400">Relatório completo</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setTocOpen((v) => !v)}
            className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl transition-colors ${tocOpen ? "bg-teal-400 text-slate-900" : "bg-white/10 text-slate-200 hover:bg-white/15"}`}
            aria-label="Índice" title="Índice">
            <ListTree className="h-5 w-5" />
          </button>
          <button onClick={onClose} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/10 text-slate-200 transition-colors hover:bg-rose-500/20 hover:text-rose-300" aria-label="Fechar" data-testid="detalhes-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* TOC (âncoras) */}
      {tocOpen && (
        <div className="shrink-0 border-b border-white/10 bg-[#04070f]/90 px-3 py-2.5 sm:px-4 max-h-[40vh] overflow-y-auto">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Ir para</p>
          <div className="flex flex-wrap gap-1.5">
            {chavesOrdenadas.map((k) => {
              const Ic = ICONES[k] || FileText;
              const c = countOf(k);
              return (
                <button key={k} onClick={() => scrollTo(`sec-${k}`)}
                  className="flex items-center gap-1.5 rounded-lg bg-white/5 px-2 py-1 text-[11px] font-semibold text-slate-200 ring-1 ring-white/10 transition-colors hover:bg-teal-400/15 hover:text-teal-100 hover:ring-teal-400/30">
                  <Ic className="h-3 w-3 text-teal-300" />
                  {TITULOS[k] || prettify(k)}
                  {c !== null && <span className="rounded-full bg-white/10 px-1 text-[9px]">{c}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* CONTEÚDO — página única, ordem dados pessoais → resto */}
      <div ref={scrollerRef} className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-4 sm:px-4">
        {dados.cadastral && (
          <div id="sec-hero" className="scroll-mt-20">
            <HeroCadastral cad={cad} foto={foto} api={api} score={scoreNum} />
          </div>
        )}
        {scoreNum && (
          <div id="sec-score" className="scroll-mt-20">
            <ScoreCard score={scoreNum} />
          </div>
        )}

        {chavesOrdenadas.map((k) => (
          <Secao key={k} id={`sec-${k}`} k={k} valor={dados[k]} api={api} count={countOf(k)} />
        ))}

        <div className="h-6" />
      </div>
    </motion.div>
  );
}
